"use client";

/**
 * AppShell — the authenticated management-page chrome for V2.
 *
 * Ported from V1's web/components/layout/AppLayout.tsx. Provides:
 *   - a fixed LEFT SIDEBAR (desktop) with logo→/, nav links, legal mini-links,
 *     and a bottom user block (avatar + name/email + live credit balance)
 *   - a top bar with a mobile hamburger
 *   - a mobile slide-in drawer mirroring the sidebar
 *
 * V2 wiring (differs from V1):
 *   - session/role via @/lib/auth/use-session (self-hosted), not next-auth
 *   - credits via @/lib/use-credits (FastAPI /credits/balance)
 *   - logo via @/components/marketing/logo-mark
 *   - cosmic-* tokens mapped to shadcn tokens (background/card/border/primary/...)
 *   - all hrefs are root-relative next/link (no basePath post-cutover)
 */

import { useEffect, useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import {
	LayoutDashboard,
	Sparkles,
	FolderKanban,
	PlayCircle,
	KeyRound,
	CreditCard,
	Coins,
	UserCog,
	ShieldCheck,
	Menu,
	X,
	LogOut,
	type LucideIcon,
} from "lucide-react";
import { Wordmark } from "@/components/brand";
import { VerifyEmailBanner } from "@/components/auth/verify-email-banner";
import { ThemeToggle } from "@/components/theme-toggle";
import { useSession, signOut } from "@/lib/auth/use-session";
import { useCredits } from "@/lib/use-credits";
import { cn } from "@/utils/ui";
import { CreditIcon } from "@/components/ui/credit-icon";

interface NavItem {
	href: string;
	icon: LucideIcon;
	label: string;
}

const NAV_ITEMS: NavItem[] = [
	{ href: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
	{ href: "/generate", icon: Sparkles, label: "Generate" },
	{ href: "/projects", icon: FolderKanban, label: "Projects" },
	{ href: "/productions", icon: PlayCircle, label: "Productions" },
	{ href: "/developers", icon: KeyRound, label: "Developers" },
	{ href: "/credits", icon: CreditCard, label: "Credits" },
	{ href: "/pricing", icon: Coins, label: "Pricing" },
	{ href: "/profile", icon: UserCog, label: "Profile" },
];

const LEGAL_LINKS = [
	{ href: "/terms", label: "Terms" },
	{ href: "/privacy", label: "Privacy" },
	{ href: "/refund", label: "Refund" },
];

/** Active when pathname matches exactly, or is a child route (href !== "/"). */
function isActive(pathname: string, href: string): boolean {
	return pathname === href || (href !== "/" && pathname.startsWith(href));
}

/**
 * True only after the first client-side effect runs. Session/credits come from
 * client-only hooks (useSession / useCredits) that may already have cached
 * values on the client's FIRST render but are always absent at SSR — so the
 * server HTML and the initial client render disagree → React #418 hydration
 * mismatch (the recurring cosmetic error on management pages). Gating every
 * session-dependent branch on `mounted` forces the initial client render to
 * match the SSR output (neutral shell), then flips to real values one tick
 * later. suppressHydrationWarning can't fix this because the admin nav ADDS an
 * element, not just changes text.
 */
function useMounted(): boolean {
	const [mounted, setMounted] = useState(false);
	useEffect(() => setMounted(true), []);
	return mounted;
}

function Brand({ onNavigate }: { onNavigate?: () => void }) {
	return (
		<Link
			href="/"
			onClick={onNavigate}
			className="flex items-center gap-2"
			aria-label="Chitravan home"
		>
			<Wordmark size={26} />
		</Link>
	);
}

function NavLink({
	item,
	active,
	onNavigate,
}: {
	item: NavItem;
	active: boolean;
	onNavigate?: () => void;
}) {
	const Icon = item.icon;
	return (
		<Link
			href={item.href}
			onClick={onNavigate}
			aria-current={active ? "page" : undefined}
			className={cn(
				"flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
				active
					? "bg-primary/15 text-primary border border-primary/30"
					: "text-muted-foreground hover:bg-accent hover:text-foreground border border-transparent",
			)}
		>
			<Icon className="size-4 shrink-0" />
			<span>{item.label}</span>
		</Link>
	);
}

function UserAvatar({
	name,
	picture,
}: {
	name?: string | null;
	picture?: string | null;
}) {
	if (picture) {
		// eslint-disable-next-line @next/next/no-img-element
		return (
			<img
				src={picture}
				alt={name ?? "User"}
				className="size-8 rounded-full object-cover"
			/>
		);
	}
	const initials = name
		? name
				.split(" ")
				.map((n) => n[0])
				.join("")
				.slice(0, 2)
				.toUpperCase()
		: "?";
	return (
		<div className="flex size-8 items-center justify-center rounded-full border border-primary/40 bg-primary/20 text-xs font-semibold text-primary">
			{initials}
		</div>
	);
}

function UserBlock() {
	const { user } = useSession();
	const credits = useCredits();
	const mounted = useMounted();
	// Until mounted, render the SSR-equivalent neutral values so hydration agrees.
	const display = mounted ? (user?.name ?? user?.email ?? "User") : "User";
	const creditsLabel =
		mounted && credits !== null ? `${credits.toLocaleString()} credits` : "… credits";
	return (
		<div className="border-t border-border px-3 py-3">
			<div className="flex items-center gap-2 text-xs text-muted-foreground">
				<UserAvatar name={mounted ? user?.name : undefined} picture={mounted ? user?.picture : undefined} />
				<div className="min-w-0 flex-1">
					{/* Values are gated behind `mounted` (see useMounted) so the
					    initial client render matches SSR — fixes React #418. */}
					<div className="truncate font-medium text-foreground" suppressHydrationWarning>{display}</div>
					<div className="text-primary" suppressHydrationWarning>{creditsLabel}</div>
				</div>
				<button
					type="button"
					onClick={() => void signOut()}
					title="Sign out"
					aria-label="Sign out"
					className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
				>
					<LogOut className="size-4" />
				</button>
			</div>
		</div>
	);
}

function SidebarBody({ onNavigate }: { onNavigate?: () => void }) {
	const pathname = usePathname();
	const { user } = useSession();
	const mounted = useMounted();
	// Admin item ADDS a nav element for admins; only add it after mount so SSR
	// (which never has a session) and the initial client render agree → no #418.
	const navItems =
		mounted && user?.role === "admin"
			? [
					...NAV_ITEMS,
					{ href: "/admin", icon: ShieldCheck, label: "Admin" } as NavItem,
				]
			: NAV_ITEMS;

	return (
		<>
			<nav className="flex-1 space-y-1 overflow-y-auto px-2 py-4">
				{navItems.map((item) => (
					<NavLink
						key={item.href}
						item={item}
						active={isActive(pathname, item.href)}
						onNavigate={onNavigate}
					/>
				))}
			</nav>

			<div className="flex flex-wrap gap-x-3 gap-y-1 px-3 py-2">
				{LEGAL_LINKS.map((l) => (
					<Link
						key={l.href}
						href={l.href}
						onClick={onNavigate}
						className="text-[10px] text-muted-foreground transition-colors hover:text-foreground"
					>
						{l.label}
					</Link>
				))}
			</div>

			<UserBlock />
		</>
	);
}

export function AppShell({ children }: { children: React.ReactNode }) {
	const [drawerOpen, setDrawerOpen] = useState(false);

	// Close mobile drawer on Escape.
	useEffect(() => {
		if (!drawerOpen) return;
		const handler = (e: KeyboardEvent) => {
			if (e.key === "Escape") setDrawerOpen(false);
		};
		document.addEventListener("keydown", handler);
		return () => document.removeEventListener("keydown", handler);
	}, [drawerOpen]);

	// Lock body scroll while the drawer is open.
	useEffect(() => {
		if (drawerOpen) {
			document.body.style.overflow = "hidden";
		} else {
			document.body.style.overflow = "";
		}
		return () => {
			document.body.style.overflow = "";
		};
	}, [drawerOpen]);

	return (
		<div className="flex h-screen overflow-hidden bg-background">
			{/* Desktop sidebar */}
			<aside className="hidden w-64 shrink-0 flex-col border-r border-border bg-card md:flex">
				<div className="flex h-14 items-center border-b border-border px-4">
					<Brand />
				</div>
				<SidebarBody />
			</aside>

			{/* Mobile drawer — always mounted for CSS transition; hidden on md+ */}
			<div
				className={cn(
					"fixed inset-0 z-40 transition-opacity duration-300 md:hidden",
					drawerOpen
						? "pointer-events-auto opacity-100"
						: "pointer-events-none opacity-0",
				)}
				aria-hidden={!drawerOpen}
			>
				<div
					className="absolute inset-0 bg-black/60"
					onClick={() => setDrawerOpen(false)}
				/>
				<aside
					className={cn(
						"relative z-50 flex h-full w-64 max-w-[80vw] transform flex-col border-r border-border bg-card transition-transform duration-300 ease-out",
						drawerOpen ? "translate-x-0" : "-translate-x-full",
					)}
				>
					<div className="flex h-14 items-center justify-between border-b border-border px-4">
						<Brand onNavigate={() => setDrawerOpen(false)} />
						<button
							type="button"
							onClick={() => setDrawerOpen(false)}
							aria-label="Close menu"
							className="rounded p-1 text-muted-foreground transition-colors hover:text-foreground"
						>
							<X className="size-4" />
						</button>
					</div>
					<SidebarBody onNavigate={() => setDrawerOpen(false)} />
				</aside>
			</div>

			{/* Main column */}
			<div className="flex min-w-0 flex-1 flex-col">
				<header className="z-30 flex h-14 shrink-0 items-center justify-between border-b border-border bg-card/80 px-4 backdrop-blur">
					<button
						type="button"
						className="text-muted-foreground transition-colors hover:text-foreground md:hidden"
						onClick={() => setDrawerOpen(true)}
						aria-label="Open menu"
					>
						<Menu className="size-5" />
					</button>
					{/* Mobile: compact brand next to hamburger; desktop: empty spacer */}
					<div className="md:hidden">
						<Brand />
					</div>
					<div className="hidden md:block" />
					<div className="flex items-center gap-2">
						{/* Light/dark toggle on every management page (V1 parity).
						    Default stays dark; this flips to light and persists. */}
						<ThemeToggle />
						<MobileCreditPill />
					</div>
				</header>

				<VerifyEmailBanner />
				<main className="flex-1 overflow-y-auto p-6">
					<div className="mx-auto w-full max-w-6xl">{children}</div>
				</main>
			</div>
		</div>
	);
}

/**
 * Top-bar credit chip — primarily for mobile where the sidebar (and its credit
 * line) is hidden behind the hamburger. Links to /credits to top up.
 */
function MobileCreditPill() {
	const credits = useCredits();
	const mounted = useMounted();
	// Show the loading pill until mounted so SSR and initial client render agree.
	const showValue = mounted && credits !== null;
	return (
		<Link
			href="/credits"
			className="inline-flex h-7 items-center gap-1.5 rounded-full border border-primary/30 bg-primary/10 px-2.5 text-xs font-mono font-semibold text-primary transition-colors hover:bg-primary/20"
			title="Credit balance — click to top up"
		>
			<CreditIcon className="size-3.5" />
			{!showValue ? (
				<span className="h-3 w-8 animate-pulse rounded bg-border/60" />
			) : (
				<span suppressHydrationWarning>{credits.toLocaleString()} cr</span>
			)}
		</Link>
	);
}
